import React, { useState } from "react";
import { useForm } from "react-hook-form";
import classnames from "classnames";
import {
  VisibilityGroup,
  CreateVisibilityGroupFormData,
  Dict,
  OnlineLeadsVisibilityGroups,
  SourceOfBusiness,
  LeadType,
  SobVisibilityGroups,
} from "../../../../core";
import { useDataLoader } from "react-remote-data-hooks";
import api from "../../../../api";
import { VisibilityGroupInput } from "../../../../components/form-group";
import { useMasterData } from "../../../../core/hooks/useMasterData";
import ErrorMessage from "../../../../components/text/ErrorMessage";

const OnlineLeadsCreateForm: React.FunctionComponent<{
  onSubmit: (data: Dict) => void;
  isSubmitting?: boolean;
  error?: string;
  data?: OnlineLeadsVisibilityGroups;
  onCancel: () => void;
  leadType?: LeadType;
}> = (props) => {
  const masterData = useMasterData(api.settings);

  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  const [sourceofBusinesses, setSourceofBusinesses] = useState<Dict>();

  const [assignVisibilityGroups, setAssignVisibilityGroups] = useState<
    VisibilityGroup[]
  >();

  const { data } = useDataLoader<VisibilityGroup[]>(() =>
    api.visibilityGroup.loadVisibilityGroup()
  );

  const [showConfirm, setShowConfirm] = React.useState(false);

  React.useEffect(() => {
    if (masterData?.data?.leadSources) {
      const entity = props.data?.leadTypes?.find(
        (x) => x.leadType?.toString() === props.leadType?._id?.toString()
      );
      setSourceofBusinesses(
        masterData?.data?.leadSources?.map((s) => {
          const sob = entity?.sourceofBusinesses?.find(
            (b) => b.sourceOfBusiness?.toString() === s._id?.toString()
          );

          return {
            sourceOfBusiness: s._id,
            sobVisibilityGroups: !!sob ? sob.sobVisibilityGroups : [],
          };
        })
      );
    }
  }, [masterData?.data?.leadSources, props.leadType]);

  const { handleSubmit } = useForm<CreateVisibilityGroupFormData>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });

  const allIds = masterData?.data?.leadSources?.map((b) => b._id!);

  const onAllSelected = (selected: boolean) => {
    if (selected) {
      setSelectedValues(allIds || []);
    } else {
      setSelectedValues([]);
    }
  };

  const allSelected =
    (selectedValues?.length || 0) > 0 &&
    !allIds?.find((id) => !selectedValues?.includes(id));

  const onSelect = (value: SourceOfBusiness, selected: boolean) => {
    if (selected) {
      setSelectedValues([...(selectedValues || []), value?._id!]);
    } else {
      setSelectedValues(
        selectedValues?.filter((val) => val !== value?._id) || []
      );
    }
  };

  const onAssign = () => {
    setSourceofBusinesses(
      sourceofBusinesses?.map((s: SobVisibilityGroups) => {
        return {
          ...s,
          sobVisibilityGroups: selectedValues?.includes(s.sourceOfBusiness)
            ? assignVisibilityGroups?.map((v) => v._id)
            : s.sobVisibilityGroups,
        };
      })
    );
    setShowConfirm(false);
    setAssignVisibilityGroups([]);
    setSelectedValues([]);
  };

  const onUpdate = () => {
    setSourceofBusinesses(
      sourceofBusinesses?.map((s: SobVisibilityGroups) => {
        return {
          ...s,
          sobVisibilityGroups: selectedValues?.includes(s.sourceOfBusiness)
            ? [
                ...(s.sobVisibilityGroups || []),
                ...(assignVisibilityGroups?.map((v) => v._id) || []),
              ]
            : s.sobVisibilityGroups,
        };
      })
    );
    setShowConfirm(false);
    setAssignVisibilityGroups([]);
    setSelectedValues([]);
  };

  const onUpdateEntity = () => {
    props.onSubmit({
      leadType: props.leadType?._id,
      sourceofBusinesses,
    });
  };

  const renderConfirmButtons = () => {
    return (
      <>
        <div
          className="modal fade show in"
          id="exampleModal"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-confirm modal-dialog-centered">
            <div className="modal-content rounded-0">
              <div className="modal-body text-center">
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <p>Please do the following</p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-4 mr-auto">
                      <button
                        type="button"
                        className="btn btn-primary py-2 font-weight-semibold font-normal px-4 mr-1"
                        onClick={onUpdate}
                      >
                        Update
                      </button>
                    </div>
                    <div className="col-4 mr-auto">
                      <button
                        type="submit"
                        className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
                        onClick={onAssign}
                      >
                        Replace
                      </button>
                    </div>
                    <div className="col-4 ">
                      <button
                        type="button"
                        className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
                        onClick={() => setShowConfirm(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show"></div>
      </>
    );
  };

  const renderSourceOfBussiness = () => {
    return (
      <>
        <div className="grid-table grid-table--sm border-top overflow-visible">
          <table>
            <thead>
              <tr>
                <th
                  className="check-all zIndex-3 px-0"
                  style={{ width: "24px" }}
                >
                  <input
                    type="checkbox"
                    id="checkall"
                    checked={allSelected}
                    onChange={(e) => onAllSelected(e.target.checked)}
                  />
                  <label htmlFor="checkall"></label>
                </th>
                <th
                  key={"SourceOfBussiness-header-column-1"}
                  className="bg-white"
                >
                  Lead Source
                </th>
                <th
                  key={"SourceOfBussiness-header-column-1"}
                  className="bg-white"
                >
                  Visibility Group
                </th>
              </tr>
            </thead>
            <tbody>
              {masterData?.data?.leadSources?.map((item, index) => {
                const value = sourceofBusinesses?.find(
                  (x: SobVisibilityGroups) => x.sourceOfBusiness === item._id
                );
                return (
                  <tr>
                    <td className="check-all px-0" style={{ width: "24px" }}>
                      <input
                        type="checkbox"
                        id={`check-${item?._id}`}
                        checked={!!selectedValues?.find((x) => x === item?._id)}
                        onChange={(e) => onSelect(item, e.target.checked)}
                      />
                      <label htmlFor={`check-${item?._id}`}></label>
                    </td>

                    <td key={"lead-import-table-data-column-1"}>{item.name}</td>
                    <td key={"lead-import-table-data-column-2"}>
                      <VisibilityGroupInput
                        defaultValue={value?.sobVisibilityGroups}
                        data={data}
                        onChange={(groups: VisibilityGroup[]) => {
                          setSourceofBusinesses(
                            sourceofBusinesses?.map(
                              (s: SobVisibilityGroups) => {
                                return {
                                  ...s,
                                  sobVisibilityGroups:
                                    item._id === s.sourceOfBusiness
                                      ? groups?.map((v) => v._id)
                                      : s.sobVisibilityGroups,
                                };
                              }
                            )
                          );
                        }}
                        sm={true}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const renderEntiryTab = () => {
    return (
      <div className="row gutter-10">
        <div className="col-12">
          <div className="form-group font-weight-semibold d-flex flex-wrap bg-gray-100 p-3 align-items-baseline">
            <label className="font-sm text-uppercase w-100">
              Visibility Group
            </label>
            <div className="col px-0">
              <VisibilityGroupInput
                data={data}
                defaultValue={assignVisibilityGroups?.map((v) => v._id)}
                onChange={(groups: VisibilityGroup[]) => {
                  setAssignVisibilityGroups(groups);
                }}
              />
            </div>
            <div className="col-auto pl-1 pr-0">
              <button
                className={classnames({
                  "btn btn-primary py-2 font-weight-semibold font-normal px-4 ml-2": true,
                })}
                disabled={
                  !selectedValues?.length || !assignVisibilityGroups?.length
                }
                type="button"
                onClick={() => {
                  const added = sourceofBusinesses?.filter(
                    (x: {
                      sourceOfBusiness: string;
                      sobVisibilityGroups: string | any[];
                    }) =>
                      selectedValues?.includes(x.sourceOfBusiness) &&
                      !!x.sobVisibilityGroups?.length
                  );

                  if (!!added?.length) {
                    setShowConfirm(true);
                  } else {
                    onAssign();
                  }
                }}
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="col-lg col-md-6 col-12 p-2 setting-detail-block left-0 top-0 bg-white w-100 h-100 zIndex-1">
      <div className="text-right d-md-none">
        <i className="icon-close"></i>
      </div>
      <div className="p-4">
        <h4 className="mb-3 font-weight-semibold">
          Online Leads{" "}
          {props.leadType && (
            <>
              {">"} Map Visibility Group for {props.leadType?.name}{" "}
            </>
          )}
        </h4>

        <div>{renderEntiryTab()}</div>
        <div>{renderSourceOfBussiness()}</div>
        <div className="col-12">
          <div className="form-group font-weight-semibold mb-4 text-right pt-3">
            <ErrorMessage>{props.error}</ErrorMessage>
            <br />
            <button
              className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
              onClick={props.onCancel}
            >
              Cancel
            </button>

            <button
              className={classnames({
                "btn btn-primary py-2 font-weight-semibold font-normal px-4": true,
              })}
              onClick={handleSubmit(onUpdateEntity)}
              disabled={props.isSubmitting}
            >
              Update
            </button>
          </div>
        </div>
      </div>
      {showConfirm && renderConfirmButtons()}
    </div>
  );
};

export default OnlineLeadsCreateForm;